import React, { useEffect, useState, useRef } from 'react';
import { Users, TrendingUp, Trophy, Network } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const TEXT = {
  de: {
    eyebrow: 'Live-Zeichnungsstand',
    title: 'Bereits gezeichnet',
    investorsLabel: 'Investoren',
    volumeLabel: 'Volumen (CHF)',
    mediatorsLabel: 'Vermittler',
    topInvestorLabel: 'Top-Investor',
    progressLabel: 'des Zielvolumens erreicht',
    noData: 'Daten werden geladen…',
    sharesLabel: 'Aktien gezeichnet',
    sharesProgressLabel: 'der ausgegebenen Aktien gezeichnet',
    aregLive: 'Live aus dem Schweizer Aktienregister',
    tranchesTitle: 'Kapitalerhöhungen / Tranchen',
    tranchesIntro: 'Status je Kapitalerhöhung – live aus dem Aktienregister',
    sharesShortLabel: 'Aktien',
    sharesNumbersLabel: 'Nr.',
    pricePerShareLabel: 'pro Aktie',
    badgeClosed: 'Abgeschlossen',
    badgeLive: '🔥 Live – jetzt zeichnen',
    badgePending: 'Geplant',
    subscribeCta: 'Jetzt zeichnen',
    handelsregisterNote: 'Im Handelsregister eingetragen ✓',
  },
  en: {
    eyebrow: 'Live Subscription Status',
    title: 'Already subscribed',
    investorsLabel: 'Investors',
    volumeLabel: 'Volume (CHF)',
    mediatorsLabel: 'Mediators',
    topInvestorLabel: 'Top Investor',
    progressLabel: 'of target volume reached',
    noData: 'Loading data…',
    sharesLabel: 'Shares subscribed',
    sharesProgressLabel: 'of issued shares subscribed',
    aregLive: 'Live from the Swiss share register',
    tranchesTitle: 'Capital Increases / Tranches',
    tranchesIntro: 'Status per capital increase – live from the share register',
    sharesShortLabel: 'Shares',
    sharesNumbersLabel: 'No.',
    pricePerShareLabel: 'per share',
    badgeClosed: 'Completed',
    badgeLive: '🔥 Live – subscribe now',
    badgePending: 'Planned',
    subscribeCta: 'Subscribe now',
    handelsregisterNote: 'Registered in the commercial register ✓',
  },
  ru: {
    eyebrow: 'Текущий статус подписки',
    title: 'Уже подписано',
    investorsLabel: 'Инвесторы',
    volumeLabel: 'Объём (CHF)',
    mediatorsLabel: 'Посредники',
    topInvestorLabel: 'Топ-инвестор',
    progressLabel: 'от целевого объёма достигнуто',
    noData: 'Загрузка данных…',
    sharesLabel: 'Акций подписано',
    sharesProgressLabel: 'от выпущенных акций подписано',
    aregLive: 'В реальном времени из швейцарского реестра акций',
    tranchesTitle: 'Увеличения капитала / Транши',
    tranchesIntro: 'Статус по каждому увеличению капитала – в реальном времени',
    sharesShortLabel: 'Акций',
    sharesNumbersLabel: '№',
    pricePerShareLabel: 'за акцию',
    badgeClosed: 'Завершено',
    badgeLive: '🔥 Активно – подписаться',
    badgePending: 'Запланировано',
    subscribeCta: 'Подписаться',
    handelsregisterNote: 'Внесено в торговый реестр ✓',
  },
  vi: {
    eyebrow: 'Tình trạng đăng ký trực tiếp',
    title: 'Đã đăng ký',
    investorsLabel: 'Nhà đầu tư',
    volumeLabel: 'Khối lượng (CHF)',
    mediatorsLabel: 'Người môi giới',
    topInvestorLabel: 'Nhà đầu tư hàng đầu',
    progressLabel: 'của khối lượng mục tiêu đã đạt được',
    noData: 'Đang tải dữ liệu…',
    sharesLabel: 'Cổ phiếu đã đăng ký',
    sharesProgressLabel: 'của cổ phiếu phát hành đã đăng ký',
    aregLive: 'Trực tiếp từ sổ đăng ký cổ đông Thụy Sĩ',
    tranchesTitle: 'Tăng vốn / Đợt phát hành',
    tranchesIntro: 'Trạng thái theo từng đợt tăng vốn – trực tiếp',
    sharesShortLabel: 'Cổ phiếu',
    sharesNumbersLabel: 'Số',
    pricePerShareLabel: 'mỗi cổ phiếu',
    badgeClosed: 'Đã hoàn thành',
    badgeLive: '🔥 Đang mở – đăng ký ngay',
    badgePending: 'Đã lên kế hoạch',
    subscribeCta: 'Đăng ký ngay',
    handelsregisterNote: 'Đã đăng ký trong sổ thương mại ✓',
  }
};

// Animate a number from 0 to target value when component enters viewport.
// Restarts when target changes so async-loaded data still animates correctly.
function useCountUp(target, durationMs = 1400) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);
  const inViewRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) inViewRef.current = true;
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (target <= 0) {
      setValue(0);
      return;
    }
    const runAnimation = () => {
      startedRef.current = true;
      const start = performance.now();
      const animate = (now) => {
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / durationMs);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(target * eased);
        if (progress < 1) requestAnimationFrame(animate);
        else setValue(target);
      };
      requestAnimationFrame(animate);
    };
    // If already in viewport (e.g. data arrived after first paint), animate immediately
    if (inViewRef.current) {
      runAnimation();
      return;
    }
    // Otherwise poll briefly until visible
    const interval = window.setInterval(() => {
      if (inViewRef.current) {
        window.clearInterval(interval);
        runAnimation();
      }
    }, 200);
    return () => window.clearInterval(interval);
  }, [target, durationMs]);

  return [ref, value];
}

const formatCHF = (n) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(Math.round(n));

const StatTile = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    data-testid={testId}
    className="bg-black/55 backdrop-blur-md border border-[#C4A55A]/25 rounded-xl p-5 hover:border-[#C4A55A]/60 transition-all duration-300"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-lg bg-[#C4A55A]/15 border border-[#C4A55A]/40 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#C4A55A]" />
      </div>
      <span className="text-gray-300 text-xs uppercase tracking-wider">{label}</span>
    </div>
    <div className={`text-3xl md:text-4xl font-bold ${accent || 'text-white'} tabular-nums`}>
      {value}
    </div>
  </div>
);

const TrancheCard = ({ tranche, text, index, lang }) => {
  const [ref, animPercent] = useCountUp(tranche.progressPercent ?? 0, 1500);
  const [, animSubscribed] = useCountUp(tranche.subscribedShares ?? 0, 1500);
  const completed = tranche.completed ?? (tranche.progressPercent ?? 0) >= 99.5;
  const isLive = !completed && (tranche.totalShares - (tranche.subscribedShares || 0)) > 0;
  const hasNumbers = tranche.numberFrom && tranche.numberTo;
  const currency = tranche.currency || 'CHF';
  const investUrl = `/?openSubscribe=1&tranche=${encodeURIComponent(tranche.pk || index)}#investors`;

  const badge = completed
    ? { label: text.badgeClosed, cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' }
    : isLive
    ? { label: text.badgeLive, cls: 'bg-[#C4A55A]/20 text-[#C4A55A] border-[#C4A55A]/50 animate-pulse' }
    : { label: text.badgePending, cls: 'bg-white/10 text-gray-300 border-white/15' };

  return (
    <div
      ref={ref}
      data-testid={`live-counter-tranche-${tranche.pk ?? index}`}
      className={`p-4 rounded-xl border transition-colors ${
        completed
          ? 'bg-emerald-500/8 border-emerald-500/40'
          : 'bg-[#C4A55A]/8 border-[#C4A55A]/30'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-white">{tranche.name}</span>
            <span
              data-testid={`tranche-badge-${tranche.pk ?? index}`}
              className={`text-[10px] uppercase tracking-wider font-semibold border px-2 py-0.5 rounded-full ${badge.cls}`}
            >
              {badge.label}
            </span>
          </div>
          <div className="text-[11px] text-gray-400">
            {tranche.totalShares.toLocaleString('de-CH')} {text.sharesShortLabel}
            {tranche.issuePrice ? (
              <>
                {' · '}
                {currency} {tranche.issuePrice.toLocaleString('de-CH', { maximumFractionDigits: 2 })} {text.pricePerShareLabel}
                {tranche.faceValue && Math.abs(tranche.issuePrice - tranche.faceValue) > 0.01 ? (
                  <span className="text-gray-500"> (Nennwert {currency} {tranche.faceValue.toLocaleString('de-CH', { maximumFractionDigits: 0 })})</span>
                ) : null}
              </>
            ) : tranche.faceValue ? (
              <>
                {' · '}
                {currency} {tranche.faceValue.toLocaleString('de-CH', { maximumFractionDigits: 2 })} {text.pricePerShareLabel}
              </>
            ) : null}
            {hasNumbers ? (
              <>
                {' · '}
                {text.sharesNumbersLabel} {tranche.numberFrom.toLocaleString('de-CH')}–{tranche.numberTo.toLocaleString('de-CH')}
              </>
            ) : null}
          </div>
        </div>
        <div className={`font-bold text-lg tabular-nums ${completed ? 'text-emerald-300' : 'text-[#C4A55A]'}`}>
          {animPercent.toFixed(1)}%
        </div>
      </div>
      <div className="flex justify-between items-end text-sm text-gray-200 mb-2">
        <span>
          <span className="font-semibold text-white tabular-nums">
            {Math.round(animSubscribed).toLocaleString('de-CH')}
          </span>{' '}
          / {tranche.totalShares.toLocaleString('de-CH')} {text.sharesLabel}
        </span>
        <span className="text-gray-400 text-xs">
          {currency} {formatCHF(tranche.subscribedValue)} · {tranche.shareholderCount} {text.investorsLabel}
        </span>
      </div>
      <div className="w-full h-2.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${
            completed
              ? 'bg-gradient-to-r from-emerald-500 to-emerald-300'
              : 'bg-gradient-to-r from-emerald-500 via-[#C4A55A] to-[#D4B56A]'
          }`}
          style={{ width: `${animPercent}%` }}
        />
      </div>
      {isLive && (
        <a
          href={investUrl}
          data-testid={`tranche-cta-${tranche.pk ?? index}`}
          className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold text-sm transition-all w-full md:w-auto"
        >
          {text.subscribeCta} →
        </a>
      )}
      {completed && (
        <div className="mt-2 text-[11px] text-emerald-300/80">
          {text.handelsregisterNote}
        </div>
      )}
    </div>
  );
};

export const LiveCounter = () => {
  const { language } = useLanguage();
  const text = TEXT[language] || TEXT.de;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const url = (apiBase ? apiBase : '') + '/api/investor-stats';
        const r = await fetch(url);
        const data = await r.json();
        if (!cancelled && data?.stats) setStats(data.stats);
      } catch (e) {
        // silently fail – the section just disappears
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hooks called unconditionally
  const [refInvestors, animInvestors] = useCountUp(stats?.investorCount ?? 0);
  const [refVolume, animVolume] = useCountUp(stats?.totalInvestment ?? 0, 1800);
  const [refMediators, animMediators] = useCountUp(stats?.mediatorCount ?? 0);
  const [refProgress, animProgress] = useCountUp(stats?.progressPercent ?? 0, 1600);
  const [refSharesProgress, animSharesProgress] = useCountUp(
    stats?.areg?.sharesProgressPercent ?? 0,
    1600
  );
  const [refSharesValue, animSharesValue] = useCountUp(stats?.areg?.subscribedShares ?? 0, 1800);

  // Hide entirely if loading failed or there is literally nothing to show yet
  if (!loading && !stats) return null;
  if (
    stats &&
    stats.investorCount === 0 &&
    stats.totalInvestment === 0 &&
    !stats.areg
  )
    return null;

  const top = stats?.topInvestor;
  const areg = stats?.areg;

  return (
    <div
      data-testid="live-counter"
      className="bg-black/60 backdrop-blur-md border border-[#C4A55A]/25 rounded-2xl p-6 md:p-8 mt-8"
    >
      <div className="flex items-center gap-2 mb-5">
        <span className="h-2 w-2 rounded-full bg-[#C4A55A] animate-pulse" />
        <span className="text-[#C4A55A] text-xs font-medium tracking-wider uppercase">
          {text.eyebrow}
        </span>
      </div>

      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
        {text.title}
      </h3>
      <p className="text-gray-400 text-sm mb-6 max-w-2xl">
        {language === 'en'
          ? 'Real-time figures from the official Swiss share register das-aktienregister.ch. Updated continuously and audited by an external register manager – maximum transparency for every investor.'
          : 'Echtzeit-Daten direkt aus dem offiziellen Schweizer Aktienregister das-aktienregister.ch. Laufend aktualisiert und von einem externen Register­verwalter geprüft – maximale Transparenz für jeden Investor.'}
      </p>

      {/* Per-tranche progress bars (one per capital increase) */}
      {areg && Array.isArray(areg.perSecurity) && areg.perSecurity.length > 0 && (
        <div className="mb-6" data-testid="live-counter-areg-tranches">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-300 text-[11px] font-medium uppercase tracking-wider">
              {text.aregLive} · {text.tranchesTitle}
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-3">{text.tranchesIntro}</p>
          <div className="space-y-3">
            {areg.perSecurity.map((tr, i) => (
              <TrancheCard key={tr.pk ?? i} tranche={tr} text={text} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Fallback: aggregated Areg progress when perSecurity is unavailable */}
      {areg && (!areg.perSecurity || areg.perSecurity.length === 0) && areg.totalShares > 0 && (
        <div
          ref={refSharesProgress}
          data-testid="live-counter-areg-shares"
          className="mb-6 p-4 rounded-xl bg-[#C4A55A]/8 border border-[#C4A55A]/30"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-300 text-[11px] font-medium uppercase tracking-wider">
                {text.aregLive}
              </span>
            </div>
            <span className="text-[#C4A55A] font-bold text-lg tabular-nums">
              {animSharesProgress.toFixed(1)}%
            </span>
          </div>
          <div className="flex justify-between items-end mb-2 text-sm text-gray-200">
            <span>
              <span className="font-semibold text-white tabular-nums">
                {Math.round(animSharesValue).toLocaleString('de-CH')}
              </span>{' '}
              / {areg.totalShares.toLocaleString('de-CH')} {text.sharesLabel}
            </span>
            <span className="text-gray-400 text-xs">
              CHF {formatCHF(areg.subscribedValue)} ({areg.shareholderCount} {text.investorsLabel})
            </span>
          </div>
          <div className="w-full h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 via-[#C4A55A] to-[#D4B56A] rounded-full transition-all duration-700 ease-out"
              style={{ width: `${animSharesProgress}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">{text.sharesProgressLabel}</p>
        </div>
      )}

      {/* Progress bar */}
      <div ref={refProgress} className="mb-8" data-testid="live-counter-progress">
        <div className="flex justify-between items-end mb-2">
          <span className="text-sm text-gray-300">
            CHF{' '}
            <span className="text-[#C4A55A] font-semibold tabular-nums">
              {formatCHF(stats?.totalInvestment ?? 0)}
            </span>{' '}
            / CHF{' '}
            <span className="text-gray-400 tabular-nums">
              {formatCHF(stats?.targetVolume ?? 0)}
            </span>
          </span>
          <span className="text-[#C4A55A] font-bold text-lg tabular-nums">
            {animProgress.toFixed(1)}%
          </span>
        </div>
        <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#B8974E] via-[#C4A55A] to-[#D4B56A] rounded-full transition-all duration-700 ease-out shadow-[0_0_20px_rgba(196,165,90,0.4)]"
            style={{ width: `${animProgress}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-2">{text.progressLabel}</p>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div ref={refInvestors}>
          <StatTile
            icon={Users}
            label={text.investorsLabel}
            value={Math.round(animInvestors)}
            accent="text-white"
            testId="live-counter-investors"
          />
        </div>
        <div ref={refVolume}>
          <StatTile
            icon={TrendingUp}
            label={text.volumeLabel}
            value={formatCHF(animVolume)}
            accent="text-[#C4A55A]"
            testId="live-counter-volume"
          />
        </div>
        <div ref={refMediators}>
          <StatTile
            icon={Network}
            label={text.mediatorsLabel}
            value={Math.round(animMediators)}
            accent="text-white"
            testId="live-counter-mediators"
          />
        </div>
        <StatTile
          icon={Trophy}
          label={text.topInvestorLabel}
          value={
            top ? (
              <div className="flex flex-col">
                <span className="text-base text-gray-200">{top.label}</span>
                <span className="text-2xl text-[#C4A55A] tabular-nums">
                  CHF {formatCHF(top.investment)}
                </span>
              </div>
            ) : (
              <span className="text-base text-gray-500">—</span>
            )
          }
          accent="text-white"
          testId="live-counter-top"
        />
      </div>
    </div>
  );
};
